import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Laptop, Loader2, LogOut, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, QueryState } from "@/components/common";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function SessionsPanel() {
  const { setUser } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["auth-sessions"], queryFn: () => api.get("/auth/sessions").then((r) => r.data) });
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(null);

  const revokeOne = async (s) => {
    setBusy(s.id);
    try { await api.delete(`/auth/sessions/${s.id}`); toast.success(`Signed out ${s.device}`); qc.invalidateQueries({ queryKey: ["auth-sessions"] }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const revokeAll = async () => {
    try {
      await api.post("/auth/sessions/revoke-all");
      qc.clear(); setUser(false);
      localStorage.removeItem("cq_ws");
      window.location.replace("/login");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Panel title="Signed-in devices" eyebrow="Security" testId="settings-sessions"
      action={<button data-testid="sign-out-everywhere-button" onClick={() => setConfirmAll(true)} className="cq-btn cq-btn-danger !py-1.5 !text-xs"><LogOut size={13} /> Sign out everywhere</button>}>
      <QueryState q={q} empty={<p className="text-sm text-[#AAB6CC]">No active sessions.</p>}>
        {(items) => (
          <div className="divide-y divide-[#172342]" data-testid="session-list">
            {items.map((s) => (
              <div key={s.id} data-testid={`session-row-${s.id}`} className="py-3 flex items-center gap-3">
                <Laptop size={16} className="text-[#7C8BAD] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#F7FAFF]">{s.device}{s.current && <span data-testid="session-current" className="ml-2 text-[11px] text-[#4BE3A4]">This device</span>}</div>
                  <div className="text-[11px] text-[#7C8BAD] font-mono">{s.ip} · signed in {fmtDateTime(s.created_at)} · {s.method === "google" ? "Google" : "Password"}</div>
                </div>
                {!s.current && <button data-testid={`session-revoke-${s.id}`} disabled={busy === s.id} onClick={() => revokeOne(s)} className="cq-btn cq-btn-ghost !py-1 !text-xs">
                  {busy === s.id ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Sign out</button>}
              </div>))}
          </div>)}
      </QueryState>
      <p className="mt-3 text-xs text-[#7C8BAD]">You get an email whenever a new device signs in, and after repeated failed sign-in attempts.</p>
      <ConfirmDialog open={confirmAll} onOpenChange={setConfirmAll} danger title="Sign out on every device?" confirmLabel="Sign out everywhere"
        description="Every session, including this one, ends immediately. You'll need to sign in again. This is recorded in the activity log." onConfirm={revokeAll} />
    </Panel>
  );
}
